import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { RotateCcw, Search } from 'lucide-react';
import { fetchBranches, fetchSuppliers, fetchSupplierReturnCatalog, createSupplierReturn } from '@/api';
import { useAuth } from '@/contexts/AuthContext';
import { useCurrencySymbol } from '@/hooks/use-currency-symbol';
import type { Supplier, SupplierReturnCatalogItem } from '@/types/supplier';

interface Branch {
  id: string;
  name: string;
  isMain: boolean;
}

interface CatalogLine extends SupplierReturnCatalogItem {
  selected: boolean;
  returnQuantity: number;
  unit_price: number;
}

interface BulkReturnDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  supplierId?: string | null;
  onCompleted?: () => void;
}

export function BulkReturnDialog({ open, onOpenChange, supplierId, onCompleted }: BulkReturnDialogProps) {
  const { user } = useAuth();
  const currencySymbol = useCurrencySymbol();
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const [branches, setBranches] = useState<Branch[]>([]);
  const [selectedBranch, setSelectedBranch] = useState('');
  const [date, setDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [reason, setReason] = useState('');
  const [catalog, setCatalog] = useState<CatalogLine[]>([]);
  const [search, setSearch] = useState('');
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDate(format(new Date(), 'yyyy-MM-dd'));
    setReason('');
    setSearch('');
    setSelectedSupplier(supplierId || '');
    fetchSuppliers().then(res => setSuppliers(res.data || [])).catch(() => setSuppliers([]));
    fetchBranches().then(res => {
      const list = res.data || [];
      setBranches(list);
      const main = list.find((b: Branch) => b.isMain);
      setSelectedBranch(main?.id || list[0]?.id || '');
    }).catch(() => setBranches([]));
  }, [open, supplierId]);

  useEffect(() => {
    if (!open || !selectedSupplier) {
      setCatalog([]);
      return;
    }
    setLoadingCatalog(true);
    fetchSupplierReturnCatalog(selectedSupplier)
      .then(res => {
        const items: SupplierReturnCatalogItem[] = res.data || [];
        setCatalog(items.map(item => ({
          ...item,
          selected: false,
          returnQuantity: 1,
          unit_price: item.latest_unit_price,
        })));
      })
      .catch(() => {
        toast.error('Failed to load this supplier\'s item history');
        setCatalog([]);
      })
      .finally(() => setLoadingCatalog(false));
  }, [open, selectedSupplier]);

  const updateLine = (itemId: string, patch: Partial<CatalogLine>) => {
    setCatalog(prev => prev.map(l => l.item_id === itemId ? { ...l, ...patch } : l));
  };

  const filteredCatalog = catalog.filter(l =>
    !search.trim() ||
    l.item_name.toLowerCase().includes(search.toLowerCase()) ||
    (l.item_sku || '').toLowerCase().includes(search.toLowerCase())
  );

  const selectedLines = catalog.filter(l => l.selected && l.returnQuantity > 0);
  const totalReturn = selectedLines.reduce((sum, l) => sum + l.returnQuantity * l.unit_price, 0);
  const supplier = suppliers.find(s => s.id === selectedSupplier);

  const handleSubmit = async () => {
    if (!selectedSupplier) {
      toast.error('Please select a supplier');
      return;
    }
    if (!selectedBranch) {
      toast.error('Please select a branch');
      return;
    }
    if (selectedLines.length === 0) {
      toast.error('Select at least one item to return');
      return;
    }
    for (const line of selectedLines) {
      if (line.unit_price < 0) {
        toast.error(`Price for "${line.item_name}" can't be negative`);
        return;
      }
    }

    setSubmitting(true);
    try {
      await createSupplierReturn({
        supplier_id: selectedSupplier,
        branch_id: selectedBranch,
        date,
        reason: reason || undefined,
        user_id: user?.id,
        items: selectedLines.map(l => ({
          item_id: l.item_id,
          quantity: l.returnQuantity,
          unit_price: l.unit_price,
        })),
      });
      toast.success('Return recorded and supplier credit updated');
      onOpenChange(false);
      onCompleted && onCompleted();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to create return');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RotateCcw className="h-5 w-5" />
            Return Items
          </DialogTitle>
          <DialogDescription>
            Pick a supplier, then select from anything they've ever supplied you. Prices
            default to the latest invoice price but stay editable. The total is credited
            toward this supplier's next invoice, and returned stock is deducted from the
            branch you select.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Supplier *</Label>
              <Select value={selectedSupplier} onValueChange={setSelectedSupplier}>
                <SelectTrigger>
                  <SelectValue placeholder="Select supplier..." />
                </SelectTrigger>
                <SelectContent>
                  {suppliers.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {supplier && (
                <p className="text-xs text-muted-foreground mt-1">
                  Current credit: {currencySymbol}{(supplier.creditBalance || 0).toFixed(2)}
                </p>
              )}
            </div>
            <div>
              <Label>Branch (stock deducted from here) *</Label>
              <Select value={selectedBranch} onValueChange={setSelectedBranch}>
                <SelectTrigger>
                  <SelectValue placeholder="Select branch..." />
                </SelectTrigger>
                <SelectContent>
                  {branches.map(b => (
                    <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Return Date</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
          </div>

          <div>
            <Label>Reason (optional)</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Expired stock" />
          </div>

          {!selectedSupplier ? (
            <div className="text-center py-10 text-muted-foreground text-sm">
              Select a supplier above to see the items they've supplied you.
            </div>
          ) : loadingCatalog ? (
            <div className="text-center py-10 text-muted-foreground text-sm">Loading item history...</div>
          ) : catalog.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground text-sm">
              No invoiced items found for this supplier.
            </div>
          ) : (
            <>
              <div className="relative max-w-sm">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search items..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              <div className="border rounded-lg overflow-hidden max-h-80 overflow-y-auto">
                <Table>
                  <TableHeader className="sticky top-0 bg-gray-50">
                    <TableRow>
                      <TableHead className="w-10"></TableHead>
                      <TableHead>Item</TableHead>
                      <TableHead className="text-center w-24">Qty</TableHead>
                      <TableHead className="text-right w-32">Unit Price</TableHead>
                      <TableHead className="text-right w-28">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredCatalog.map(line => (
                      <TableRow key={line.item_id}>
                        <TableCell>
                          <Checkbox
                            checked={line.selected}
                            onCheckedChange={(checked) => updateLine(line.item_id, { selected: !!checked })}
                          />
                        </TableCell>
                        <TableCell>
                          <div className="font-medium">{line.item_name}</div>
                          {line.item_sku && <div className="text-xs text-muted-foreground">SKU: {line.item_sku}</div>}
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            min="1"
                            value={line.returnQuantity === 0 ? '' : line.returnQuantity}
                            onChange={(e) => updateLine(line.item_id, { returnQuantity: e.target.value === '' ? 0 : Math.max(0, parseInt(e.target.value, 10) || 0) })}
                            className="h-8 w-20 text-center mx-auto"
                            disabled={!line.selected}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={line.unit_price}
                            onChange={(e) => updateLine(line.item_id, { unit_price: parseFloat(e.target.value) || 0 })}
                            className="h-8 w-28 text-right ml-auto"
                            disabled={!line.selected}
                          />
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {currencySymbol}{(line.selected ? line.returnQuantity * line.unit_price : 0).toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div className="flex justify-end text-lg font-semibold">
                Return Total: {currencySymbol}{totalReturn.toFixed(2)}
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting || !selectedSupplier}>
            {submitting ? 'Processing...' : 'Create Return'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
